import Flex from 'components/Flex';
import { StyledInput } from 'components/Form/Input';
import Label from 'components/Form/Label';
import StyledErrorMessage from 'components/Form/StyledErrorMessage';
import Wrapper from 'components/Form/Wrapper';
import { ErrorMessage, FieldProps, getIn } from 'formik';
import { ReactComponent as Close } from 'icons/close.svg';
import { ReactComponent as Calendar } from 'icons/date-calendar.svg';
import { formatDate } from 'libs/dateTimeHelpers';
import React, { FunctionComponent, useCallback, useRef, useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import styled from 'styled-components';
import '../style.css';

interface ISingleDatePicker {
  className?: string;
  disabled?: boolean;
  inputProps?: any;
  label?: string;
  dayPickerProps?: DayPickerProps;
  showClose?: boolean;
}

const SingleDatePicker: FunctionComponent<FieldProps & ISingleDatePicker> = ({
  className,
  field: { value, onChange, onBlur, name, ...input },
  form,
  inputProps,
  label,
  dayPickerProps,
  showClose = false,
  ...rest
}) => {
  const [overlayClass, setOverlayClass] = useState<string>('DayPickerInput-Overlay');
  const wrapEl = useRef(null);
  const handleOnChange = useCallback((selected) => form.setFieldValue(name, selected), []);
  const handleReset = useCallback(() => form.setFieldValue(name, undefined), []);
  const handleDisplayFormat = useCallback(
    (date?: Date) => (date ? formatDate(date, 'yyyy-MM-dd') : ''),
    []
  );

  const handleOnShow = useCallback(() => {
    const overlayHeight = 261;
    const overlayClass =
      //@ts-ignore
      wrapEl && document.body.clientHeight < wrapEl.current?.offsetTop + overlayHeight
        ? 'DayPickerInput-Overlay Bottom'
        : 'DayPickerInput-Overlay';
    setOverlayClass(overlayClass);
  }, [wrapEl, setOverlayClass]);

  return (
    <Wrapper className={className} ref={wrapEl}>
      <DateWrapper>
        {label && <Label>{label}</Label>}
        <DayPickerInput
          value={value}
          onDayChange={handleOnChange}
          onDayPickerShow={handleOnShow}
          dayPickerProps={{
            selectedDays: value,
            ...dayPickerProps,
          }}
          format={''}
          formatDate={handleDisplayFormat}
          component={StyledInput}
          classNames={{
            container: 'DayPickerInput-Container',
            overlay: overlayClass,
            overlayWrapper: 'DayPickerInput-OverlayWrapper',
          }}
          inputProps={{
            autoComplete: 'off',
            disabled: rest.disabled,
            ...inputProps,
            ...input,
            error: Boolean(getIn(form.touched, name) && getIn(form.errors, name)),
            name,
            onBlur: form.handleBlur(name),
            placeholder: 'YYYY-MM-DD',
            readOnly: true,
          }}
        />
        {value && showClose && (
          <CloseWrapper onClick={handleReset}>
            <Close />
          </CloseWrapper>
        )}
        <Icon />
      </DateWrapper>
      <ErrorMessage name={name} component={StyledErrorMessage} />
    </Wrapper>
  );
};

export default SingleDatePicker;

const Icon = styled(Calendar)`
  position: absolute;
  right: 15px;
  bottom: 10px;
`;

const DateWrapper = styled.div`
  position: relative;
`;

const CloseWrapper = styled(Flex)<{ onClick: () => void }>`
  position: absolute;
  right: 50px;
  bottom: 10px;
  cursor: pointer;
  &:hover {
    ${(props) => props.theme.fillSvg(props.theme.colors.accentNormal)};
  }
`;
